import { toPlainText } from "../components/rich-text";
import { getLadderStatus } from "./ladder-progress";
import NYCGenericBadge from "@/assets/certificates/nyc-badge-generic.png";
import GenericWorkforceBadge from "@/assets/certificates/generic-workforce-certificate-badge.png";

import jsPDF from "jspdf";
export const getCompletedAwards = (awards, allCareerClimbs, currentUser) => {
  return awards.filter((award) =>
    checkAwardCompletion(award, allCareerClimbs, currentUser),
  );
};

export const checkAwardCompletion = (award, allCareerClimbs, currentUser) => {
  const climbsForAward = getClimbsForAward(award, allCareerClimbs);

  return climbsForAward.every((climbId) =>
    hasCompletedClimb(climbId, allCareerClimbs, currentUser),
  );
};

export const getHardCodedBadgeAndSkills = (award) => {
  let badge;
  let skills;
  let estimatedCertTime;

  const YYSkills = [
    "Career Exploration",
    "Career Planning",
    "Money Management",
    "Professional Communication",
    "Worker Rights",
    "Conflict Management",
    "Adaptability",
  ];
  const OYCOSkills = [
    "Career Exploration",
    "Career Planning",
    "Job Searching",
    "Money Management",
    "Professional Communication",
    "Worker Rights",
    "Time Management",
    "Conflict Management",
    "Digital Mindset",
  ];
  const OYCRSkills = [
    "Career Exploration",
    "Career Planning",
    "Job Searching",
    "Resumes & Interviews",
    "Money Management",
    "Professional Communication",
    "Worker Rights",
    "Conflict Management",
    "Workplace Ethics",
    "Digital Mindset",
  ];

  if (award.awardType === "certificate") {
    switch (award?.name) {
      case "SYEP YY Community":
        skills = YYSkills;
        estimatedCertTime = 3;
        break;

      case "SYEP YY Career Ready":
        skills = YYSkills;
        estimatedCertTime = 5;
        break;

      case "SYEP OY Community":
        skills = OYCOSkills;
        estimatedCertTime = 5;
        break;

      default:
        skills = OYCRSkills;
        estimatedCertTime = 6;
    }

    if (award.name.includes("Community")) {
      badge = NYCGenericBadge;
    } else if (award.name.includes("Career")) {
      badge = NYCGenericBadge;
    } else {
      badge = GenericWorkforceBadge;
    }

    // badge = award.name.includes("Community") ? SYEPBadgeCO : SYEPBadgeCR;
  }
  return { badge, skills, estimatedCertTime };
};

export const hasCompletedClimb = (climbId, allCareerClimbs, currentUser) => {
  const careerClimb = allCareerClimbs.find(
    (climb) => String(climb.id) === String(climbId),
  );
  const ladderProgress = currentUser.ladderProgress;
  if (!careerClimb) return false;
  if (!ladderProgress) return false;
  const ladders = careerClimb.ladders;
  return ladders.every(
    (ladder) => getLadderStatus(ladder, ladderProgress) === "COMPLETED",
  );
};

// Returns all the climb ID's that match the award type
export const getClimbsForAward = (award, allCareerClimbs) => {
  const { awardRule } = award;
  const { requiredClimbs } = award;

  switch (awardRule) {
    case "all_climbs_in_plan":
      return getAllClimbsInPlan(award.productPlan[0]?.code, allCareerClimbs);
    case "career_climb_list":
      return requiredClimbs;
    // case "all_climbs_in_course": // NEEDS WORK!
    //   return;
    default:
      console.error("Unknown award rule ", awardRule);
      return [];
  }
};

const getAllClimbsInPlan = (planCode, allCareerClimbs) => {
  return allCareerClimbs
    .filter((climb) => climb.productPlan.some((p) => p.code === planCode))
    .map((climb) => climb.id);
};

export const downloadCertificate = (award, currentUser, completedAt) => {
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });

  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const margin = 20; // 20mm margins
  const maxWidth = pageWidth - 2 * margin;

  // Set up the document
  doc.setFontSize(30);
  doc.setFont("helvetica", "bold");
  doc.setTextColor(28, 37, 46);

  // Title - reduced top padding from 40 to 30
  doc.text("Congratulations", pageWidth / 2, 30, { align: "center" });

  // Recipient name
  doc.setFontSize(24);
  doc.text(
    `${currentUser.firstName} ${currentUser.lastName}`,
    pageWidth / 2,
    50,
    {
      align: "center",
    },
  );

  // Award details
  doc.setFontSize(16);
  doc.text("You've earned a certificate for", pageWidth / 2, 70, {
    align: "center",
  });
  doc.setFont("helvetica", "bold");
  doc.text(award.title, pageWidth / 2, 80, { align: "center" });

  let yPosition = 100; // Adjusted starting position for content

  // Create an image element to get the dimensions
  const img = new Image();
  img.src = award.image.url;

  // Wait for image to load before adding to PDF
  return new Promise((resolve) => {
    img.onload = () => {
      // Calculate image dimensions to fit within margins while maintaining aspect ratio
      const maxImageWidth = 60; // Maximum width in mm
      const maxImageHeight = 60; // Maximum height in mm

      let imageWidth = maxImageWidth;
      let imageHeight = (img.height * maxImageWidth) / img.width;

      if (imageHeight > maxImageHeight) {
        imageHeight = maxImageHeight;
        imageWidth = (img.width * maxImageHeight) / img.height;
      }

      // Center the image horizontally
      const imageX = (pageWidth - imageWidth) / 2;

      doc.addImage(img, imageX, yPosition, imageWidth, imageHeight);

      yPosition += imageHeight + 10; // Add some spacing after the image

      // Add completion message if available
      if (award.completionMessage) {
        doc.setFontSize(12);
        doc.setFont("helvetica", "normal");

        const lines = doc.splitTextToSize(
          toPlainText(award.completionMessage),
          maxWidth,
        );
        const lineHeight = 6;

        lines.forEach((line, index) => {
          doc.text(line, pageWidth / 2, yPosition + index * lineHeight, {
            align: "center",
          });
        });
      }

      // Add completion date in bottom right
      if (completedAt) {
        doc.setFontSize(10);
        doc.setFont("helvetica", "italic");
        const dateText = `Completed on ${new Date(completedAt).toLocaleDateString()}`;
        doc.text(dateText, pageWidth - margin, pageHeight - margin / 2, {
          align: "right",
        });
      }

      // Generate and open PDF
      const pdfDataUri = doc.output("bloburl");
      window.open(pdfDataUri, "_blank");
      resolve();
    };
  });
};
